/**
 * Old-funnel -> new-calculator forwarding map.
 *
 * The old funnel/client-account routes are translated per locale (baked into
 * `src/translations/main.json`), while the new calculator uses fixed English
 * routes on a different domain. This module resolves an incoming (translated)
 * pathname to the matching new-calculator path.
 *
 * It is a pure function so it can be unit-tested in isolation; the actual
 * cross-domain redirect (window.location) is wired up in `gatsby-browser.js`
 * and only runs when `config.shouldRedirectToNewCalculator` is true.
 *
 * Scope (see migration ticket) is limited to the "ready" rows: the funnel
 * entry points, the client-account pages, purchase-premium and the affiliate
 * page. Anything not matched returns `null` so unknown routes are left
 * untouched.
 *
 * Cobranding: all partners share one build on `claim.claimservice.app`, so the
 * destination host is not known here — it comes from the partner config that
 * `gatsby-browser.js` resolves (see `src/utils/partnerConfig.ts`). This module
 * only needs to know *that* we are on the cobranding build, via
 * `options.isCobranding`, which turns the funnel entry points into
 * `/get-compensation/<dossierToken>` deep links.
 */

import { parse } from 'qs';

export interface RouteTranslations {
  [key: string]: string | undefined;
}

/** Parsed query string, as produced by `qs.parse`. */
interface QueryParams {
  [key: string]: undefined | string | string[] | QueryParams | QueryParams[];
}

interface RedirectRule {
  pattern: RegExp;
  build: (match: string[], query: QueryParams, search: string) => string;
}

/** Extra context that changes how some rules resolve. */
export interface ResolveOptions {
  /**
   * True when the request is served by the shared cobranding build
   * (`claim.claimservice.app`), i.e. a `partner` could be resolved. Turns the
   * funnel entry points into `/get-compensation/<dossierToken>` deep links.
   */
  isCobranding?: boolean;
}

const CLAIM_TARGET = '/claim';

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Remove a trailing slash but keep the root "/" intact. */
const stripTrailingSlash = (path: string): string =>
  path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;

/**
 * Normalise a raw `location.search` to something safe to append to a path:
 * "?a=1" and "a=1" both become "?a=1", empty stays empty.
 */
const normalizeSearch = (search: string): string => {
  const trimmed = (search || '').replace(/^\?/, '');
  return trimmed ? `?${trimmed}` : '';
};

/** Read a query param only when it is a single string (arrays are ignored). */
const singleParam = (query: QueryParams, key: string): string => {
  const value = query[key];
  return typeof value === 'string' ? value : '';
};

/**
 * Target for the funnel entry points. On the shared cobranding build a
 * `dossierToken` turns the entry point into the partner's deep link, so a
 * bookmarked `/congratulations?partner=…&dossierToken=ABC` resumes the claim
 * instead of dropping the customer at the top of the funnel.
 */
const funnelTarget = (isCobranding: boolean) => (
  match: string[],
  query: QueryParams
): string => {
  if (isCobranding) {
    const dossierToken = singleParam(query, 'dossierToken');
    if (dossierToken) {
      return `/get-compensation/${encodeURIComponent(dossierToken)}`;
    }
  }
  return CLAIM_TARGET;
};

/**
 * Build the ordered list of redirect rules from the current site's route
 * translations. Order matters: more specific claim sub-routes must be tested
 * before the claim base route.
 */
const buildRules = (
  t: RouteTranslations,
  options: ResolveOptions = {}
): RedirectRule[] => {
  const toFunnel = funnelTarget(Boolean(options.isCobranding));
  const seg = (key: string, fallback: string): string =>
    escapeRegExp(t[key] || fallback);

  const account = seg('cc4.seo.route.account', 'account');
  const claim = seg('cc4.seo.route.shareable-claim-page', 'claim');

  const claimBase = `^/${account}/${claim}/([^/]+)`;

  return [
    // --- Client account: claim detail sub-pages (claim number preserved) ---
    {
      pattern: new RegExp(
        `${claimBase}/${seg(
          'cc4.seo.route.claims-booking-details',
          'booking-details'
        )}$`
      ),
      build: m => `/account/claims/${m[1]}/booking`,
    },
    {
      pattern: new RegExp(
        `${claimBase}/${seg('cc4.seo.route.documents', 'documents')}$`
      ),
      build: m => `/account/claims/${m[1]}/documents`,
    },
    {
      pattern: new RegExp(
        `${claimBase}/${seg(
          'cc4.seo.route.claims-flight-itinerary',
          'flight-itinerary'
        )}$`
      ),
      build: m => `/account/claims/${m[1]}/flights`,
    },
    {
      pattern: new RegExp(
        `${claimBase}/${seg(
          'cc4.seo.route.passengers-list',
          'passengers-list'
        )}$`
      ),
      build: m => `/account/claims/${m[1]}/passengers`,
    },
    // Claim detail base (must come after the sub-pages above)
    {
      pattern: new RegExp(`${claimBase}$`),
      build: m => `/account/claims/${m[1]}`,
    },

    // --- Client account: simple pages under the account basepath ---
    {
      pattern: new RegExp(
        `^/${account}/${seg('cc4.seo.route.my-profile', 'my-profile')}$`
      ),
      build: () => '/account/profile',
    },
    {
      pattern: new RegExp(
        `^/${account}/${seg('cc4.seo.route.password', 'password')}$`
      ),
      build: () => '/account/profile/password',
    },
    {
      pattern: new RegExp(
        `^/${account}/${seg('cc4.seo.route.help-center', 'help')}$`
      ),
      build: () => '/account/support',
    },
    {
      pattern: new RegExp(
        `^/${account}/${seg(
          'cc4.account-page.help.message-contact-us-route',
          'contact-us'
        )}$`
      ),
      build: () => '/account/support',
    },
    {
      pattern: new RegExp(
        `^/${account}/${seg('cc4.seo.route.my-premium', 'my-premium')}$`
      ),
      build: () => '/account/subscription',
    },

    // --- Client account: top-level login / forgot-password ---
    {
      pattern: new RegExp(`^/${seg('cc4.seo.route.login', 'login')}$`),
      build: () => '/account/login',
    },
    {
      pattern: new RegExp(
        `^/${seg('cc4.seo.route.forgot-password', 'forgot-password')}$`
      ),
      build: () => '/account/forgot-password',
    },
    {
      pattern: new RegExp(
        `^/${seg('cc4.seo.route.set-new-password', 'set-new-password')}$`
      ),
      build: () => '/account/login',
    },

    // --- Premium ---
    {
      pattern: new RegExp(
        `^/${seg('cc4.seo.route.purchase-premium', 'purchase-premium')}$`
      ),
      build: () => '/account/subscription/checkout',
    },

    // --- Shareable claim page (task list) ---
    // Old links (bookmarked / emailed) carry the auth token as `?token=`;
    // the new calculator expects it as `?claim_token=` on /app/claim-tasks.
    {
      pattern: new RegExp(`^/${claim}/([^/]+)$`),
      build: (m, query) => {
        const base = `/app/claim-tasks/${m[1]}`;
        const token = typeof query.token === 'string' ? query.token : '';
        return token
          ? `${base}?claim_token=${encodeURIComponent(token)}`
          : base;
      },
    },

    // --- Affiliate ---
    // The only rule that carries the WHOLE query string across: the new
    // affiliate page needs dossierToken, email and name, and forwarding them
    // selectively would mean re-encoding params we do not own. `partner` /
    // `lang` ride along too, which is harmless.
    // Deliberately kept out of the funnel-entry group below so the cobranding
    // `dossierToken` deep link cannot swallow it (that would drop email/name).
    {
      pattern: new RegExp(`^/${seg('cc4.seo.route.affiliate', 'affiliate')}$`),
      build: (m, query, search) => `/affiliate${normalizeSearch(search)}`,
    },

    // --- Funnel entry points ---
    // Forward to /claim without prefill, or to the partner's
    // /get-compensation/<dossierToken> deep link on the cobranding build.
    { pattern: /^\/$/, build: toFunnel },
    {
      pattern: new RegExp(`^/${seg('cc4.seo.route.itinerary', 'itinerary')}$`),
      build: toFunnel,
    },
    {
      pattern: new RegExp(
        `^/${seg('cc4.seo.route.check-compensation', 'check-compensation')}$`
      ),
      build: toFunnel,
    },
    // congratulations + any sub-path (e.g. .../permission)
    {
      pattern: new RegExp(
        `^/${seg(
          'cc4.seo.route.congratulations-basepath',
          'congratulations'
        )}(?:/.*)?$`
      ),
      build: toFunnel,
    },
    {
      pattern: new RegExp(
        `^/${seg('cc4.seo.route.abandoned-cart', 'finish-your-claim')}$`
      ),
      build: toFunnel,
    },
    {
      pattern: new RegExp(
        `^/${seg('cc4.seo.route.cobranding', 'cobranding')}$`
      ),
      build: toFunnel,
    },
  ];
};

/**
 * Resolve an incoming (translated) pathname to the matching new-calculator
 * path, or `null` when the route is out of migration scope.
 *
 * @param pathname  location.pathname (no query string / hash)
 * @param translations  the site's route translations (`translations/main.json`)
 * @param search  location.search (e.g. "?token=abc"); used by rules that need
 *   to carry a query param across, such as the shareable claim page token.
 * @param options  see `ResolveOptions` — `isCobranding` turns the funnel entry
 *   points into `/get-compensation/<dossierToken>` deep links.
 */
export const resolveRedirectTarget = (
  pathname: string,
  translations: RouteTranslations,
  search = '',
  options: ResolveOptions = {}
): string | null => {
  const normalized = stripTrailingSlash(pathname || '/') || '/';
  const query = parse(search, { ignoreQueryPrefix: true });

  for (const rule of buildRules(translations, options)) {
    const match = normalized.match(rule.pattern);
    if (match) {
      return rule.build(match, query, search);
    }
  }

  return null;
};

/**
 * Host portion of a configured `newCalculatorDomain`.
 *
 * The value may carry a path, because sites whose new funnel lives under a
 * locale folder configure it as e.g. "www.flight-delayed.com/en". Comparing
 * `window.location.host` against the raw value would then never match, silently
 * disabling the redirect-loop guard — hence this helper.
 */
export const getTargetHost = (domain: string): string =>
  (domain || '').replace(/^https?:\/\//, '').split('/')[0];

/**
 * Absolute https URL for a resolved target path.
 *
 * `domain` is a host, optionally with a path prefix ("www.flight-delayed.com/en")
 * and tolerant of a scheme or trailing slash.
 */
export const buildTargetUrl = (domain: string, target: string): string => {
  const base = (domain || '').replace(/^https?:\/\//, '').replace(/\/+$/, '');
  const path = target.startsWith('/') ? target : `/${target}`;
  return `https://${base}${path}`;
};

/**
 * The two shareable pages are a special case: the new calculator needs the CLAIM
 * number in its path, but these old URLs do not always carry it. The claim then
 * has to be fetched from the API with the auth token, which is async — so the
 * two matchers below only *detect* the page and extract what the caller needs
 * for that lookup. They deliberately live outside the synchronous `buildRules`
 * list.
 *
 * Both pages are entered without an id: the emailed link is `/passenger?token=`
 * / `/claim?token=`, and the old app resolves the passenger (or claim number)
 * itself before redirecting on to `/passenger/:id/login` etc. Those follow-up
 * paths match no rule, so failing to catch the id-less form leaves the visitor
 * stranded on the old app.
 */

/** Read the shareable-page auth token out of a raw query string. */
const shareableToken = (search: string): string => {
  const query = parse(search, { ignoreQueryPrefix: true });
  return typeof query.token === 'string' ? query.token : '';
};

/**
 * Shareable passenger page, with (`/passenger/:passengerId`) or without
 * (`/passenger`) the passenger id — the id is not needed for the target, only
 * the claim number the token resolves to.
 *
 * @returns `{ token }` when the pathname is the shareable passenger page,
 *   otherwise `null`.
 */
export const matchShareablePassengerPage = (
  pathname: string,
  translations: RouteTranslations,
  search = ''
): { token: string } | null => {
  const normalized = stripTrailingSlash(pathname || '/') || '/';
  const passenger = escapeRegExp(
    translations['cc4.seo.route.passenger'] || 'passenger'
  );

  if (!new RegExp(`^/${passenger}(?:/([^/]+))?$`).test(normalized)) {
    return null;
  }

  return { token: shareableToken(search) };
};

/**
 * Shareable claim page entered *without* a claim number (`/claim?token=`). The
 * `/claim/:claimNumber` form needs no lookup and is handled synchronously by
 * `resolveRedirectTarget`, so it is deliberately not matched here.
 *
 * @returns `{ token }` when the pathname is the id-less shareable claim page,
 *   otherwise `null`.
 */
export const matchShareableClaimPage = (
  pathname: string,
  translations: RouteTranslations,
  search = ''
): { token: string } | null => {
  const normalized = stripTrailingSlash(pathname || '/') || '/';
  const claim = escapeRegExp(
    translations['cc4.seo.route.shareable-claim-page'] || 'claim'
  );

  if (!new RegExp(`^/${claim}$`).test(normalized)) {
    return null;
  }

  return { token: shareableToken(search) };
};

/** `/app/claim-tasks/<claimNumber>`, with the auth token under `tokenParam`. */
const claimTasksTarget = (
  claimNumber: string,
  tokenParam: string,
  token: string
): string => {
  const base = `/app/claim-tasks/${claimNumber}`;
  return token ? `${base}?${tokenParam}=${encodeURIComponent(token)}` : base;
};

/**
 * Build the new-calculator target for a resolved shareable passenger claim.
 * The auth token is carried across as `?passenger_token=` (renamed from the
 * old `?token=`), mirroring the shareable claim page's `claim_token`.
 */
export const buildPassengerRedirectTarget = (
  claimNumber: string,
  token: string
): string => claimTasksTarget(claimNumber, 'passenger_token', token);

/**
 * Build the new-calculator target for a resolved id-less shareable claim page.
 * Uses the same `?claim_token=` the path-based claim rule emits, so both entry
 * forms of that page land identically.
 */
export const buildClaimRedirectTarget = (
  claimNumber: string,
  token: string
): string => claimTasksTarget(claimNumber, 'claim_token', token);
